import type { FC } from 'react';
import { useEffect, useState, lazy, Suspense } from 'react';

import DesktopFooter from './desktop';
import MobileFooter from './mobile';

import { observer } from 'mobx-react';
// рендерится по условию → lazy (уедет из main вместе с импортом в MainHeader)
const CallbackModal = lazy(async () => await import('@components/Modals/CallbackModal'));
import SuccessModalWithTimer from '@components/Modals/SuccessModalWithTimer';
import useWindowSize from '@customHooks/useWindowSize';
import Subscription from '@components/Subscription';
import CreateOrderFooter from './components/CreateOrderFooter';

import './index.scss';
import { getLocalStorageItem } from '@utils/getLocalStorageItem';
import type { RouteProps } from '@compat/router';
import { useStores } from '@root/src/stores';

interface FooterProps {
  className?: string;
  path: RouteProps['path'];
}

const Footer: FC<FooterProps> = observer(({ className, path }) => {
  const { userStore } = useStores();
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const { windowWidth } = useWindowSize();

  const closeCallbackModal = (): void => {
    setShowCallbackModal(false);
  };

  const openCallbackModal = (): void => {
    setShowCallbackModal(true);
  };

  const closeSuccessModal = (): void => {
    setShowSuccessModal(false);
  };

  const changeToSuccessModal = (): void => {
    setShowCallbackModal(false);
    setShowSuccessModal(true);
    setSuccessModalText('Мы перезвоним вам в самое ближайшее время');
  };

  const isShowSubscriptionFooter = getLocalStorageItem<boolean>(
    'isShowSubscriptionFooter'
  );

  const isShowCreateOrderFooter = path === '/cart/order';

  const shouldShowSubscription =
    userStore.isSubscribedToPromo === false && !isShowSubscriptionFooter;

  useEffect(() => {
    const enableByInteraction = (): void => {
      setHasUserInteracted(true);
    };

    window.addEventListener('keydown', enableByInteraction, { once: true });
    window.addEventListener('wheel', enableByInteraction, {
      once: true,
      passive: true
    });
    window.addEventListener('pointerdown', enableByInteraction, {
      once: true,
      passive: true
    });

    return () => {
      window.removeEventListener('keydown', enableByInteraction);
      window.removeEventListener('wheel', enableByInteraction);
      window.removeEventListener('pointerdown', enableByInteraction);
    };
  }, []);

  const shouldShowSubscriptionAfterScroll =
    hasUserInteracted && shouldShowSubscription;

  return (
    <footer className={`main-footer ${className || ''}`}>
      {shouldShowSubscriptionAfterScroll ? (
        <div className='main-footer__subscription-slot main-footer__subscription-slot--appear'>
          <Subscription />
        </div>
      ) : null}
      {showCallbackModal && (
        <Suspense fallback={null}>
          <CallbackModal
            onClose={closeCallbackModal}
            changeToSuccessModal={changeToSuccessModal}
          />
        </Suspense>
      )}
      {showSuccessModal && (
        <SuccessModalWithTimer
          closeModal={closeSuccessModal}
          title='Внимание!'
          text={successModalText}
        />
      )}
      {isShowCreateOrderFooter ? (
        <CreateOrderFooter />
      ) : windowWidth >= 768 ? (
        <DesktopFooter openCallbackModal={openCallbackModal} />
      ) : (
        <MobileFooter openCallbackModal={openCallbackModal} />
      )}
    </footer>
  );
});

export default Footer;
